import React from 'react';
import Typography from '@material-ui/core/Typography';
import ArrowIcon from '@material-ui/icons/ArrowForwardRounded';
import { withStyles, Paper, Grid, ButtonBase, Hidden } from '@material-ui/core';
import Link from '../misc/Link';
import ArrowButton from '../misc/ArrowButton';
import { trackLink, trackEvent, AnalyticsConstants } from '../../../utilities/analyticsUtilities';
import { getConfigurationProperty } from '../../../configuration';

const styles = theme => ({
	'@keyframes rotate-cw': {
		from: {transform: 'rotate(0deg) translate(-50%, -50%)'},
		to: {transform: 'rotate(359deg) translate(-50%, -50%)'},
	},
	'@keyframes rotate-ccw': {
		from: {transform: 'rotate(359deg) translate(-50%, -50%)'},
		to: {transform: 'rotate(0deg) translate(-50%, -50%)'},
	},
	'@keyframes gradient': {
		'0%': {backgroundPosition: '0% 50%'},
		'50%': {backgroundPosition: '100% 50%'},
		'100%': {backgroundPosition: '0% 50%'},
	},
    section1: {
		height: 667,
	},
	textItem: {
		[theme.breakpoints.down('sm')]: {
			height: '40%',
		},
	},
	headerText: {
		[theme.breakpoints.down('sm')]: {
			fontSize: '2.5rem',
		},
		[theme.breakpoints.up('md')]: {
			fontSize: '3.2rem',
		},
		textShadow: '1px 1px 2px #000000',
		padding: theme.spacing(2)
	},
	subHeaderText: {
		fontSize: '1.3rem',
		textShadow: '1px 1px 2px #000000',
		padding: theme.spacing(2)
	},
	primaryButton: {
		margin: theme.spacing(2),
		zIndex: 1
	},
	buttonIcon: {
		marginLeft: theme.spacing(1)
	},
	videoItem: {
		position: 'relative',
		[theme.breakpoints.down('sm')]: {
			height: '50%',
		},
		[theme.breakpoints.up('md')]: {
			height: '100%',
		},
	},
	videoContainerContainer: {
        [theme.breakpoints.down('sm')]: {
			width: '100%',
		},
		[theme.breakpoints.up('md')]: {
			width: '90%',
		},
		maxWidth: 480,
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)'
	},
	videoContainer: {
		height: 0,
		paddingTop: '56.25%',
		position: 'relative',
		'&:hover': {
            '& a': {
                opacity: 1
            }
		},
		cursor: 'pointer'
	},
	video: {
		width: '100%',
		height: '100%',
		position: 'absolute',
		top: 0,
		left: 0
	},
	videoLink: {
		opacity: 0,
		transition: theme.transitions.create('opacity', {
			easing: theme.transitions.easing.easeInOut,
			duration: theme.transitions.duration.shorter,
		}),
	},
	videoOverlay: {
		width: '100%',
		height: '100%',
		position: 'absolute',
		top: 0,
		left: 0,
		backgroundColor: 'rgba(0,0,0,0.85)',
		color: 'white'
	},
	wave: {
		position: 'absolute',
		zIndex: -1,
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		transformOrigin: 'top left',
		[theme.breakpoints.down('sm')]: {
			width: 550,
			height: 550,
		},
		[theme.breakpoints.up('md')]: {
			width: 725,
			height: 725,
		},
	},
	wave1: {
		mask: 'url(https://specterr.b-cdn.net/wave1.svg) no-repeat',
		animation: '$rotate-cw 120s infinite linear, $gradient 30s linear infinite',
		background: 'linear-gradient(270deg, #0d47a1, #1a237e, #311b92, #4a148c, #880e4f, #b71c1c, #bf360c, #e65100, #ff6f00, #f57f17, #827717, #33691e, #1b5e20, #004d40, #006064, #01579b)',
		backgroundSize: '600% 600%',
	},
	wave2: {
		mask: 'url(https://specterr.b-cdn.net/wave2.svg) no-repeat',
		animation: '$rotate-ccw 120s infinite linear',
		backgroundColor: 'rgba(255,255,255,0.15)',
	}
});

const createAVideoId = 'index-head-section-create-a-video-button';

class IndexHeadSection extends React.Component {
	render() {
		const { classes } = this.props;

		return (
            <Grid
                container
                alignItems='center'
                className={classes.section1}
            >
                <Grid 
                    item 
                    xs={12}
                    md={6}
                    className={classes.textItem}
                >
                    <Typography
                        variant='h1'
                        className={classes.headerText}
                    >
                        Turn your music into a killer video
                    </Typography>
                    <Typography
                        className={classes.subHeaderText}
						color='textSecondary'
                    >
						Specterr makes it fast and easy to create custom music visualizers. Start growing your audience with video.
                    </Typography>
                    <Hidden smDown>
                        <ArrowButton
                            href={getConfigurationProperty('CreateVideoEndpoint')}
                            color='primary'
                            variant='contained'
                            size='large'
							className={classes.primaryButton}
							id={createAVideoId}
                        >
                            Create A Video
                        </ArrowButton>
                    </Hidden>
                    <Hidden mdUp>
                        <Link
                            to='/music-visualizer/#presets'
							underline='none'
							onClick={() => {
								trackEvent('Clicked CTA', {
									location: 'home head',
									destination: 'music-visualizer presets',
									color: 'red',
									text: 'See Music Visaulizers',
									type: 'button',
									category: AnalyticsConstants.ACQUISITION_CATEGORY
								});
							}}
                        >
                            <ArrowButton
                                color='primary'
                                variant='contained'
                                size='large'
                                className={classes.primaryButton}
                            >
                                See Music Visaulizers
                            </ArrowButton>
                        </Link>
                    </Hidden>
                </Grid>
                <Grid 
                    item
                    className={classes.videoItem}
                    xs={12}
                    md={6}
                >
                    <span
                        className={`${classes.wave} ${classes.wave2}`}
                    ></span>
                    <span
                        className={`${classes.wave} ${classes.wave1}`}
                    ></span>
                    <div className={classes.videoContainerContainer}>
                        <Paper className={classes.videoContainer}>
                            <video
                                autoPlay
                                muted
								playsInline
                                loop
                                poster='https://specterr.b-cdn.net/specterr-landing-tn2.jpeg'
								className={classes.video}
								alt='Preview video of audio visualizers.'
                            >
                                <source 
                                    src='https://specterr.b-cdn.net/specterr-landing.mp4'
                                    type='video/mp4'
                                />
                                Sorry, your browser doesn't support embedded videos.
                            </video>
							<Hidden smDown>
								<Link
									to='/music-visualizer/#presets'
									className={classes.videoLink}
									onClick={() => {
										trackEvent('Clicked CTA', {
											location: 'home head',
											destination: 'music-visualizer presets',
											color: 'black',
											text: 'See Music Visaulizers',
											type: 'special button',
											category: AnalyticsConstants.ACQUISITION_CATEGORY
										});
									}}
								>
									<ButtonBase className={classes.videoOverlay}>
										<Typography variant='h6'>
											See Music Visaulizers
										</Typography>
										<ArrowIcon className={classes.buttonIcon} />
									</ButtonBase>
								</Link>
							</Hidden>
                        </Paper>
                    </div>
                </Grid>
            </Grid>
		);
	}

	componentDidMount() {
		setTimeout(() => {
			trackLink(createAVideoId, 'Clicked CTA', {
				location: 'home head',
				destination: 'create',
				color: 'red',
				text: 'CREATE A VIDEO',
				type: 'button',
				category: AnalyticsConstants.ACQUISITION_CATEGORY
			});
		}, 100);
	}
}

export default withStyles(styles)(IndexHeadSection);